import * as React from "react";
import { useRef, useState, useEffect } from "react";
import { Area, Bar, CartesianGrid, Cell, ComposedChart, LabelList, Line, ReferenceLine, XAxis, YAxis } from "recharts";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/shadcn/chart";
import { type Tournament, TournamentMatchesXg } from "@/api/tournaments";
import { TournamentLinePerformanceChart } from "./TournamentLinePerformanceChart";
import { MoveUpRight, MoveDownRight, MoveRight } from "lucide-react";
import { Loader } from "../common/loader";
import { useSelectedTeam } from "@/context/teamContext";
import { useIsMobile } from "@/hooks/use-mobile";

const chartConfig = {
    goals: {
        label: "Goals Scored",
        color: "blue",
    },
    goalsAgainst: {
        label: "Goals Against",
        color: "red",
    },
    points: {
        label: "Points Earned",
        color: "green",
    },
    goalDifference: {
        label: "Goal Difference",
        color: "yellow",
    },
    highRiskShots: {
        label: "High Risk Shots",
        color: "purple",
    },
    highRiskShotsAgainst: {
        label: "High Risk Shots Against",
        color: "orange",
    },
    xgAndKpiAnalysis: {
        label: "KPI vs Goal Difference",
        color: "blue",
    },
    goalkeeperPerformance: {
        label: "Shots saved %",
        color: "grey",
    },
} satisfies ChartConfig;

const kpiOptions = [
    { value: "goals", label: "Goals Scored", shortLabel: "Goals", color: "blue" },
    { value: "goalsAgainst", label: "Goals Against", shortLabel: "GA", color: "red" },
    { value: "points", label: "Points Earned", shortLabel: "Points", color: "green" },
    { value: "goalDifference", label: "Goal Difference", shortLabel: "GD", color: "yellow" },
    { value: "highRiskShots", label: "High Risk Shots", shortLabel: "HRS", color: "purple" },
    { value: "highRiskShotsAgainst", label: "High Risk Shots Against", shortLabel: "HRSA", color: "orange" },
    { value: "xgAndKpiAnalysis", label: "KPI vs Goal Difference", shortLabel: "KPI", color: "blue" },
    { value: "goalkeeperPerformance", label: "Shots saved %", shortLabel: "GK", color: "cyan" },
    { value: "linePerformance", label: "Line Performance", shortLabel: "Lines", color: "blue" },
];

const CustomLabel = ({
    x,
    y,
    value,
    index,
    dataKey,
    data,
}: {
    x?: string | number;
    y?: string | number;
    value?: string | number | null | boolean;
    index?: number;
    dataKey?: string;
    data?: Array<{ [key: string]: number | string }>;
}) => {
    if (!value && value !== 0) return null;
    if (index === undefined || !data || !x || !y) return null;

    const xCoord = typeof x === "string" ? parseFloat(x) : x;
    const yCoord = typeof y === "string" ? parseFloat(y) : y;
    const numValue = typeof value === "string" ? parseFloat(value) : value;

    if (typeof numValue !== "number" || isNaN(xCoord) || isNaN(yCoord) || isNaN(numValue)) return null;

    const previousValue = index !== undefined && index > 0 && data && dataKey ? data[index - 1]?.[dataKey] : null;
    let direction: "up" | "down" | "same" | null = null;
    let trend: "up" | "down" | "same" | null = null;

    if (previousValue !== null && previousValue !== undefined && typeof previousValue === "number") {
        if (numValue > previousValue) direction = "up";
        else if (numValue < previousValue) direction = "down";
        else direction = "same";

        if (dataKey === "goalsAgainst" || dataKey === "highRiskShotsAgainst") {
            if (numValue < previousValue) trend = "up";
            else if (numValue > previousValue) trend = "down";
            else trend = "same";
        } else {
            trend = direction;
        }
    }

    const getTrendIcon = () => {
        switch (direction) {
            case "up":
                return <MoveUpRight className="w-4 h-4" />;
            case "down":
                return <MoveDownRight className="w-4 h-4" />;
            case "same":
                return <MoveRight className="w-4 h-4" />;
            default:
                return null;
        }
    };

    const getTrendColor = () => {
        switch (trend) {
            case "up":
                return "var(--color-general-green)";
            case "down":
                return "var(--color-general-red)";
            case "same":
                return "var(--color-gray-500)";
            default:
                return "var(--color-gray-500)";
        }
    };

    const displayValue =
        dataKey === "goalDifference" && typeof numValue === "number"
            ? numValue > 0
                ? `+${numValue}`
                : numValue.toString()
            : numValue.toString();

    const finalX = xCoord || 0;
    const finalY = yCoord || 0;

    return (
        <g transform={`translate(${finalX}, ${finalY})`}>
            <text
                x={0}
                y={-15}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={getTrendColor()}
                fontSize={14}
                fontWeight="bold"
                style={{
                    userSelect: "none",
                }}
            >
                {displayValue}
            </text>
            {trend && (
                <foreignObject
                    x={-10}
                    y={-40}
                    width={20}
                    height={20}
                    style={{
                        overflow: "visible",
                    }}
                >
                    <div
                        style={{
                            color: getTrendColor(),
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        {getTrendIcon()}
                    </div>
                </foreignObject>
            )}
        </g>
    );
};

const ValueLabel = ({
    x,
    y,
    value,
    yOffset = -16,
}: {
    x?: string | number;
    y?: string | number;
    value?: string | number | null | boolean;
    color: string;
    yOffset?: number;
}) => {
    if (value === null || value === undefined) return null;
    if (x === undefined || x === null || y === undefined || y === null) return null;

    const xCoord = typeof x === "string" ? parseFloat(x) : x;
    const yCoord = typeof y === "string" ? parseFloat(y) : y;
    if (isNaN(xCoord) || isNaN(yCoord)) return null;

    return (
        <text
            x={xCoord}
            y={yCoord + yOffset}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="rgba(203, 213, 225, 0.95)"
            fontSize={10}
            fontWeight="normal"
            style={{ userSelect: "none" }}
        >
            {String(value)}
        </text>
    );
};

interface TournamentAnalysisChartProps {
    tournamentData?: Tournament;
    matchXgs: TournamentMatchesXg | undefined;
    isLoading?: boolean;
    tournamentId?: number;
    onTitleChange?: (title: { main: string; sub: string; subColor?: string }) => void;
}

export function TournamentAnalysisChart({
    tournamentData: propTournamentData,
    matchXgs,
    isLoading,
    tournamentId,
    onTitleChange,
}: TournamentAnalysisChartProps) {
    const { teamId: coreTeamId } = useSelectedTeam();
    const [selectedKPI, setSelectedKPI] = React.useState("xgAndKpiAnalysis");
    const isMobile = useIsMobile();
    const navRef = useRef<HTMLDivElement>(null);
    const [navWidth, setNavWidth] = useState(Infinity);

    useEffect(() => {
        const el = navRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width;
            if (w !== undefined) setNavWidth(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const useShortLabels = isMobile || navWidth < 3000;

    const selectedKPIConfig = kpiOptions.find((option) => option.value === selectedKPI);

    useEffect(() => {
        onTitleChange?.({
            main: "Match-by-Match Performance Trends",
            sub: useShortLabels ? (selectedKPIConfig?.label ?? "") : "",
            subColor:
                useShortLabels && selectedKPIConfig ? `var(--color-general-${selectedKPIConfig.color})` : undefined,
        });
    }, [useShortLabels, selectedKPIConfig, onTitleChange]);

    const filteredData = React.useMemo(() => {
        if (!propTournamentData?.matches || propTournamentData.matches.length === 0) return [];

        const matches = propTournamentData.matches
            .filter(
                (match) =>
                    typeof match.homeGoals === "number" &&
                    typeof match.awayGoals === "number" &&
                    typeof match.shotsAgainstHome === "number" &&
                    typeof match.shotsAgainstAway === "number" &&
                    match.homeGoals >= 0 &&
                    match.awayGoals >= 0 &&
                    match.shotsAgainstHome >= 0 &&
                    match.shotsAgainstAway >= 0
            )
            .sort((a, b) => {
                const aDate = a.date ?? "";
                const bDate = b.date ?? "";
                return new Date(aDate).getTime() - new Date(bDate).getTime();
            });

        if (matches.length === 0) return [];

        const data = matches.map((match, index) => {
            const isCoreTeamHome = match.homeTeam.id === coreTeamId;
            const goalsScored = isCoreTeamHome ? match.homeGoals : match.awayGoals;
            const goalsConceded = isCoreTeamHome ? match.awayGoals : match.homeGoals;

            const highRiskShotsScored = isCoreTeamHome ? match.shotsAgainstAway : match.shotsAgainstHome;
            const highRiskShotsConceded = isCoreTeamHome ? match.shotsAgainstHome : match.shotsAgainstAway;

            const opponent = isCoreTeamHome ? match.awayTeam : match.homeTeam;

            const matchXg = matchXgs?.find((xg) => xg.matchId === match.id);
            const kpi = (isCoreTeamHome ? matchXg?.homeKpi : matchXg?.awayKpi) || 0;
            const xg = (isCoreTeamHome ? matchXg?.homeXg : matchXg?.awayXg) || 0;
            const opponentXg = (isCoreTeamHome ? matchXg?.awayXg : matchXg?.homeXg) || 0;
            const opponentShots = isCoreTeamHome
                ? (matchXg?.awayGoals || 0) + (matchXg?.homeSaves || 0)
                : (matchXg?.homeGoals || 0) + (matchXg?.awaySaves || 0);

            const matchPoints = matchXg?.points ?? 0;

            const goalDifference = goalsScored - goalsConceded;

            return {
                date: new Date(match.date ?? "").toLocaleDateString("en-GB"),
                matchNumber: index + 1,
                goals: goalsScored,
                goalsAgainst: goalsConceded,
                points: matchPoints,
                goalDifference: goalDifference,
                highRiskShots: highRiskShotsScored,
                highRiskShotsAgainst: highRiskShotsConceded,
                opponent: opponent.shortName,
                result: matchPoints === 3 ? "W" : matchPoints === 2 ? "WO" : matchPoints === 1 ? "LO" : "L",
                score: `${goalsScored}-${goalsConceded}`,
                highRiskShotsScore: `${highRiskShotsScored}-${highRiskShotsConceded}`,
                matchDate: match.date ?? "",
                kpi: kpi,
                xg: Math.round((xg - opponentXg) * 100) / 100,
                opponentXg: opponentXg,
                opponentShots: opponentShots,
                savedShotsPct: opponentShots
                    ? Math.round(((opponentShots - goalsConceded) / opponentShots) * 100) / 100
                    : 0,
            };
        });

        return data;
    }, [propTournamentData, matchXgs, coreTeamId]);

    if (isLoading && !propTournamentData) {
        return (
            <div className="flex items-center justify-center h-[400px]">
                <Loader message="Loading performance..." />
            </div>
        );
    }

    if ((!isLoading && !propTournamentData) || filteredData.length === 0) {
        return (
            <div className="flex items-center justify-center h-[400px]">
                <p className="text-on-surface-variant">No performance data available</p>
            </div>
        );
    }

    const averageSavedShotsPct =
        Math.round((filteredData.reduce((sum, row) => sum + row.savedShotsPct, 0) / filteredData.length) * 100) / 100;

    return (
        <div className="flex flex-col h-full">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 shrink-0 px-4 pt-3 pb-2 md:justify-end">
                <nav
                    id="onboarding-tournament-chart-tabs"
                    ref={navRef}
                    className="rounded-md p-1 bg-surface-container-low basis-full md:basis-auto"
                >
                    <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:flex-wrap md:gap-1">
                        {kpiOptions.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => setSelectedKPI(option.value)}
                                className={`nav-tab ${
                                    selectedKPI === option.value ? "text-white shadow-sm" : "nav-tab-inactive"
                                }`}
                                style={
                                    selectedKPI === option.value
                                        ? { backgroundColor: `var(--color-general-${option.color})` }
                                        : {}
                                }
                            >
                                {useShortLabels ? option.shortLabel : option.label}
                            </button>
                        ))}
                    </div>
                </nav>
            </div>

            <div className="flex-1 min-h-0">
                {selectedKPI === "linePerformance" ? (
                    <TournamentLinePerformanceChart
                        tournamentId={tournamentId || 0}
                        teamId={coreTeamId || 0}
                        tournamentMatches={propTournamentData?.matches}
                    />
                ) : (
                    <div className="overflow-x-auto overflow-y-hidden h-full">
                        <ChartContainer
                            config={chartConfig}
                            className="h-full w-full"
                            style={{
                                minWidth: isMobile
                                    ? `${Math.max(
                                          300,
                                          filteredData.length *
                                              (selectedKPI === "xgAndKpiAnalysis" ||
                                              selectedKPI === "goalkeeperPerformance"
                                                  ? 44
                                                  : 28)
                                      )}px`
                                    : "100%",
                            }}
                        >
                            <ComposedChart data={filteredData} margin={{ top: 40, right: 30, left: 30, bottom: 5 }}>
                                <defs>
                                    <linearGradient id={`fill${selectedKPI}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop
                                            offset="5%"
                                            stopColor={`var(--color-general-${selectedKPIConfig?.color})`}
                                            stopOpacity={0.8}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor={`var(--color-general-${selectedKPIConfig?.color})`}
                                            stopOpacity={0.1}
                                        />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    vertical={false}
                                    stroke="rgba(148, 163, 184, 0.1)"
                                    strokeDasharray="3 3"
                                />
                                <XAxis
                                    dataKey="date"
                                    tickLine={false}
                                    axisLine={false}
                                    tickMargin={8}
                                    tick={
                                        isMobile || filteredData.length > 20
                                            ? (props: { x?: number; y?: number; index?: number }) => (
                                                  <text
                                                      x={props.x}
                                                      y={(props.y ?? 0) + 12}
                                                      fill="var(--color-gray-400)"
                                                      fontSize={isMobile ? 8 : 10}
                                                      textAnchor="middle"
                                                  >
                                                      {(props.index ?? 0) + 1}
                                                  </text>
                                              )
                                            : { fill: "var(--color-gray-400)", fontSize: 10 }
                                    }
                                    type="category"
                                    interval={0}
                                    padding={{ left: 20, right: 20 }}
                                />
                                <ChartTooltip
                                    cursor={{
                                        stroke: `var(--color-general-${selectedKPIConfig?.color})`,
                                        strokeWidth: 2,
                                        strokeDasharray: "5 5",
                                        strokeOpacity: 0.6,
                                    }}
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;

                                        const dataPoint = payload[0]?.payload;
                                        if (!dataPoint) return null;

                                        const value = payload[0]?.value;
                                        const displayValue =
                                            selectedKPI === "goalDifference" && typeof value === "number" && value > 0
                                                ? `+${value}`
                                                : String(value);

                                        return (
                                            <div className="chart-tooltip">
                                                <div className="flex flex-col gap-2">
                                                    <div className="font-semibold text-sm text-general-blue">
                                                        Match {dataPoint.matchNumber} - {dataPoint.date}
                                                    </div>
                                                    <div className="text-xs text-on-surface flex items-center gap-2">
                                                        <span>vs {dataPoint.opponent}</span>
                                                        <span
                                                            className={`px-2 py-1 rounded text-xs font-bold ${
                                                                dataPoint.result === "W" || dataPoint.result === "WO"
                                                                    ? "bg-win-background text-win"
                                                                    : dataPoint.result === "LO"
                                                                      ? "bg-draw-background text-draw"
                                                                      : "bg-loss-background text-loss"
                                                            }`}
                                                        >
                                                            {dataPoint.result} {dataPoint.score}
                                                        </span>
                                                    </div>
                                                    {(selectedKPI === "highRiskShots" ||
                                                        selectedKPI === "highRiskShotsAgainst") && (
                                                        <div className="text-xs text-on-surface">
                                                            High Risk Shots: {dataPoint.highRiskShotsScore}
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                                                        <div
                                                            className="w-3 h-3 rounded-full"
                                                            style={{
                                                                backgroundColor: `var(--color-general-${selectedKPIConfig?.color})`,
                                                            }}
                                                        />
                                                        <span className="text-sm font-medium text-on-surface">
                                                            {selectedKPI === "xgAndKpiAnalysis"
                                                                ? "KPI"
                                                                : selectedKPIConfig?.label}
                                                            :
                                                        </span>
                                                        <span
                                                            className="text-lg font-bold"
                                                            style={{
                                                                color: `var(--color-general-${selectedKPIConfig?.color})`,
                                                            }}
                                                        >
                                                            {displayValue}
                                                        </span>
                                                    </div>
                                                    {selectedKPI === "xgAndKpiAnalysis" && (
                                                        <>
                                                            <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{
                                                                        backgroundColor: `var(--color-general-green)`,
                                                                    }}
                                                                />
                                                                <span className="text-sm font-medium text-on-surface">
                                                                    Goal difference:
                                                                </span>
                                                                <span
                                                                    className="text-lg font-bold"
                                                                    style={{ color: `var(--color-general-green)` }}
                                                                >
                                                                    {payload[1]?.value}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{
                                                                        backgroundColor: `var(--color-general-orange)`,
                                                                    }}
                                                                />
                                                                <span className="text-sm font-medium text-on-surface">
                                                                    XG difference:
                                                                </span>
                                                                <span
                                                                    className="text-lg font-bold"
                                                                    style={{ color: `var(--color-general-orange)` }}
                                                                >
                                                                    {payload[2]?.value}
                                                                </span>
                                                            </div>
                                                        </>
                                                    )}
                                                    {selectedKPI === "goalkeeperPerformance" && (
                                                        <>
                                                            <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{
                                                                        backgroundColor: `var(--color-general-yellow)`,
                                                                    }}
                                                                />
                                                                <span className="text-sm font-medium text-on-surface">
                                                                    Shot against:
                                                                </span>
                                                                <span
                                                                    className="text-lg font-bold"
                                                                    style={{ color: `var(--color-general-yellow)` }}
                                                                >
                                                                    {payload[1]?.value}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{
                                                                        backgroundColor: `var(--color-general-red)`,
                                                                    }}
                                                                />
                                                                <span className="text-sm font-medium text-on-surface">
                                                                    Opponent goals:
                                                                </span>
                                                                <span
                                                                    className="text-lg font-bold"
                                                                    style={{ color: `var(--color-general-red)` }}
                                                                >
                                                                    {payload[2]?.value}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{
                                                                        backgroundColor: `var(--color-general-orange)`,
                                                                    }}
                                                                />
                                                                <span className="text-sm font-medium text-on-surface">
                                                                    Opponent XG:
                                                                </span>
                                                                <span
                                                                    className="text-lg font-bold"
                                                                    style={{ color: `var(--color-general-orange)` }}
                                                                >
                                                                    {payload[3]?.value}
                                                                </span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                                {selectedKPI !== "xgAndKpiAnalysis" && selectedKPI !== "goalkeeperPerformance" && (
                                    <Area
                                        dataKey={selectedKPI}
                                        type="linear"
                                        fill={`url(#fill${selectedKPI})`}
                                        stroke={`var(--color-general-${selectedKPIConfig?.color})`}
                                        strokeWidth={3}
                                        dot={{
                                            r: 4,
                                            fill: `var(--color-general-${selectedKPIConfig?.color})`,
                                            strokeWidth: 0,
                                        }}
                                        activeDot={{
                                            r: 6,
                                            fill: `var(--color-general-${selectedKPIConfig?.color})`,
                                            strokeWidth: 2,
                                            stroke: "white",
                                        }}
                                    >
                                        <LabelList
                                            dataKey={selectedKPI}
                                            position="top"
                                            content={({ value, index, x, y }) => (
                                                <CustomLabel
                                                    value={value}
                                                    index={index}
                                                    x={x}
                                                    y={y}
                                                    dataKey={selectedKPI}
                                                    data={filteredData}
                                                />
                                            )}
                                        />
                                    </Area>
                                )}
                                {selectedKPI === "xgAndKpiAnalysis" && (
                                    <>
                                        <YAxis
                                            dataKey="kpi"
                                            yAxisId="kpiAxis"
                                            tickCount={8}
                                            label={{
                                                value: "Total KPI",
                                                offset: 15,
                                                position: "top",
                                                fill: "var(--color-gray-400)",
                                                fontSize: 12,
                                            }}
                                            domain={([min, max]) => [
                                                Math.floor(min / 50) * 50 - 50,
                                                Math.ceil(max / 50) * 50 + 50,
                                            ]}
                                            tick={{ fill: "var(--color-gray-400)", fontSize: 11 }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            dataKey="xg"
                                            yAxisId="goalsAxis"
                                            tickCount={6}
                                            label={{
                                                value: "Goal difference",
                                                offset: 15,
                                                position: "top",
                                                fill: "var(--color-gray-400)",
                                                fontSize: 12,
                                            }}
                                            orientation="right"
                                            domain={() => {
                                                const min = filteredData.reduce(
                                                    (p, c) => Math.min(p, c.xg, c.goalDifference),
                                                    0
                                                );
                                                const max = filteredData.reduce(
                                                    (p, c) => Math.max(p, c.xg, c.goalDifference),
                                                    0
                                                );
                                                return [min - 2, max + 2];
                                            }}
                                            tick={{ fill: "var(--color-gray-400)", fontSize: 11 }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <Bar dataKey="kpi" yAxisId="kpiAxis" radius={[4, 4, 0, 0]}>
                                            {filteredData.map((row) => (
                                                <Cell
                                                    key={`cell-${row.matchDate}-${row.opponent}`}
                                                    fill={
                                                        row.result === "L" || row.result === "LO"
                                                            ? "var(--color-general-red)"
                                                            : "var(--color-general-blue)"
                                                    }
                                                    opacity={0.85}
                                                />
                                            ))}
                                            <LabelList
                                                position="top"
                                                offset={10}
                                                fill="var(--color-gray-600)"
                                                fontSize={12}
                                                fontWeight="bold"
                                            />
                                        </Bar>
                                        <Line
                                            dataKey="goalDifference"
                                            yAxisId="goalsAxis"
                                            type="monotoneX"
                                            stroke="var(--color-general-green)"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: "var(--color-general-green)", strokeWidth: 0 }}
                                            activeDot={{
                                                r: 6,
                                                fill: "var(--color-general-green)",
                                                strokeWidth: 2,
                                                stroke: "white",
                                            }}
                                        >
                                            <LabelList
                                                dataKey="goalDifference"
                                                content={({ x, y, value }) => (
                                                    <ValueLabel
                                                        x={x}
                                                        y={y}
                                                        value={value}
                                                        color="var(--color-general-green)"
                                                        yOffset={-16}
                                                    />
                                                )}
                                            />
                                        </Line>
                                        <Line
                                            dataKey="xg"
                                            yAxisId="goalsAxis"
                                            type="monotoneX"
                                            stroke="var(--color-general-orange)"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: "var(--color-general-orange)", strokeWidth: 0 }}
                                            activeDot={{
                                                r: 6,
                                                fill: "var(--color-general-orange)",
                                                strokeWidth: 2,
                                                stroke: "white",
                                            }}
                                        >
                                            <LabelList
                                                dataKey="xg"
                                                content={({ x, y, value }) => (
                                                    <ValueLabel
                                                        x={x}
                                                        y={y}
                                                        value={value}
                                                        color="var(--color-general-orange)"
                                                        yOffset={16}
                                                    />
                                                )}
                                            />
                                        </Line>
                                    </>
                                )}
                                {selectedKPI === "goalkeeperPerformance" && (
                                    <>
                                        <YAxis
                                            dataKey="savedShotsPct"
                                            yAxisId="barAxis"
                                            tickCount={6}
                                            label={{
                                                value: "Shots saved",
                                                offset: 15,
                                                position: "top",
                                                fill: "var(--color-gray-400)",
                                                fontSize: 12,
                                            }}
                                            tick={{ fill: "var(--color-gray-400)", fontSize: 11 }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            dataKey="opponentShots"
                                            yAxisId="goalsAxis"
                                            tickCount={6}
                                            label={{
                                                value: "Shots against",
                                                offset: 15,
                                                position: "top",
                                                fill: "var(--color-gray-400)",
                                                fontSize: 12,
                                            }}
                                            orientation="right"
                                            domain={() => {
                                                const min = filteredData.reduce(
                                                    (p, c) => Math.round(Math.min(p, c.goalsAgainst, c.opponentShots)),
                                                    0
                                                );
                                                const max = filteredData.reduce(
                                                    (p, c) => Math.round(Math.max(p, c.goalsAgainst, c.opponentShots)),
                                                    0
                                                );
                                                return [min - 2, max + 4];
                                            }}
                                            tick={{ fill: "var(--color-gray-400)", fontSize: 11 }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <Bar
                                            dataKey="savedShotsPct"
                                            yAxisId="barAxis"
                                            fill="var(--color-general-cyan)"
                                            opacity={0.75}
                                            radius={[4, 4, 0, 0]}
                                        >
                                            <LabelList
                                                position="top"
                                                offset={10}
                                                fill="var(--color-gray-600)"
                                                fontSize={12}
                                                fontWeight="bold"
                                            />
                                        </Bar>
                                        <ReferenceLine
                                            y={averageSavedShotsPct}
                                            yAxisId="barAxis"
                                            stroke="var(--color-gray-400)"
                                            strokeDasharray="6 3"
                                            strokeOpacity={0.6}
                                        />
                                        <Line
                                            dataKey="opponentShots"
                                            yAxisId="goalsAxis"
                                            type="monotoneX"
                                            stroke="var(--color-general-yellow)"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: "var(--color-general-yellow)", strokeWidth: 0 }}
                                            activeDot={{
                                                r: 6,
                                                fill: "var(--color-general-yellow)",
                                                strokeWidth: 2,
                                                stroke: "white",
                                            }}
                                        >
                                            <LabelList
                                                dataKey="opponentShots"
                                                content={({ x, y, value }) => (
                                                    <ValueLabel
                                                        x={x}
                                                        y={y}
                                                        value={value}
                                                        color="var(--color-general-yellow)"
                                                        yOffset={-16}
                                                    />
                                                )}
                                            />
                                        </Line>
                                        <Line
                                            dataKey="goalsAgainst"
                                            yAxisId="goalsAxis"
                                            type="monotoneX"
                                            stroke="var(--color-general-red)"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: "var(--color-general-red)", strokeWidth: 0 }}
                                            activeDot={{
                                                r: 6,
                                                fill: "var(--color-general-red)",
                                                strokeWidth: 2,
                                                stroke: "white",
                                            }}
                                        >
                                            <LabelList
                                                dataKey="goalsAgainst"
                                                content={({ x, y, value }) => (
                                                    <ValueLabel
                                                        x={x}
                                                        y={y}
                                                        value={value}
                                                        color="var(--color-general-red)"
                                                        yOffset={16}
                                                    />
                                                )}
                                            />
                                        </Line>
                                        <Line
                                            dataKey="opponentXg"
                                            yAxisId="goalsAxis"
                                            type="monotoneX"
                                            stroke="var(--color-general-orange)"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: "var(--color-general-orange)", strokeWidth: 0 }}
                                            activeDot={{
                                                r: 6,
                                                fill: "var(--color-general-orange)",
                                                strokeWidth: 2,
                                                stroke: "white",
                                            }}
                                        >
                                            <LabelList
                                                dataKey="opponentXg"
                                                content={({ x, y, value }) => (
                                                    <ValueLabel
                                                        x={x}
                                                        y={y}
                                                        value={value}
                                                        color="var(--color-general-orange)"
                                                        yOffset={-16}
                                                    />
                                                )}
                                            />
                                        </Line>
                                    </>
                                )}
                            </ComposedChart>
                        </ChartContainer>
                    </div>
                )}
            </div>

            {selectedKPI !== "xgAndKpiAnalysis" && selectedKPI !== "linePerformance" && (
                <div className="data-table-header shrink-0 flex items-center justify-between px-5 py-2.5 border-t border-outline-variant/20">
                    <span className="font-headline font-black text-[10px] uppercase tracking-widest">
                        Avg {selectedKPIConfig?.label}
                    </span>
                    <span
                        className="font-headline font-black text-sm"
                        style={{ color: `var(--color-general-${selectedKPIConfig?.color})` }}
                    >
                        {(() => {
                            if (selectedKPI === "goalkeeperPerformance") {
                                return `${averageSavedShotsPct}`;
                            }
                            if (filteredData.length === 0) return "0";
                            const values = filteredData.map(
                                (item) => item[selectedKPI as keyof (typeof filteredData)[0]]
                            );
                            const numericValues = values.filter((val) => typeof val === "number") as number[];
                            if (numericValues.length === 0) return "0";
                            const average = numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length;
                            if (selectedKPI === "goalDifference") {
                                return average > 0 ? `+${average.toFixed(1)}` : average.toFixed(1);
                            }
                            if (selectedKPI === "points") {
                                return `${average.toFixed(1)} pts/game`;
                            }
                            return `${average.toFixed(1)} per game`;
                        })()}
                    </span>
                </div>
            )}
        </div>
    );
}
